import axios from "axios";
import { useCallback, useEffect, useState, useContext } from "react";
import { useLocation } from "@gatsbyjs/reach-router";

import { useAuth } from "@context/AuthProvider";
import { LuxeContext } from "@context/LuxeProvider";

import { removeQueryParam } from "@helpers/browser";
import { withALErrorBoundary, sendALErrorReport } from "@helpers/ErrorBoundary/ALErrorBoundary";

import { useDebounce } from "@hooks/useDebounce";
import { ALButtonV2, ALLink } from "@components/ALComponents";
import ALPopup from "@components/al_components/ALPopup";
import IconClose from "@svgs/close.svg";

const LUXE_POPUP_QUERY_PARAM = "reactivate";

function LuxeReactivationPopup() {
  const location = useLocation();
  const [isOpenLuxeReactivation, setOpenLuxeReactivation] = useState(false);
  const [isReactivated, setIsReactivated] = useState(false);

  // Pull account info to check if user is eligible for reactivation
  const { accountDetails } = useAuth();
  const { luxe, acceptChurnOffer } = useContext(LuxeContext);

  async function reactivateLuxe({ cid, email, scid }) {
    console.log("Attempting to reactivate Luxe for customer", { cid, email, scid });
    const res = await axios.post(
      "/.netlify/functions/subscription",
      {},
      {
        params: { action: "REACTIVATE", cid, email, scid },
      }
    );
    if (res.status === 200) {
      if (res.result === "error") {
        window.exponea.track("reactivation_luxe", {
          source: "luxe_reactivation_popup",
          status: "error",
          error: res?.error?.toString(),
        });
        sendALErrorReport(
          {
            message: "Failed to reactivate",
          },
          {
            name: "LuxeReactivationPopup",
            priority: "P1",
          }
        );
        return false;
      }
      const updatedSubscription = await axios.get(`/.netlify/functions/subscription`, {
        params: {
          cid: accountDetails.id.replace("gid://shopify/Customer/", ""),
          email: accountDetails.email,
        },
      });
      console.log(updatedSubscription, " => updatedSubscription");

      if (
        updatedSubscription.status === 200 &&
        updatedSubscription.data.membership.status === "ACTIVE"
      ) {
        await acceptChurnOffer({
          scid,
          offerId: luxe.offers.churnOffers?.[0]?.offers?.[0]?.id,
        }).then((response) => {
          if (response.ok) {
            setIsReactivated(true);
            window.exponea.track("reactivation_luxe", {
              source: "luxe_reactivation_popup",
              status: "accepted",
            });
          } else {
            window.exponea.track("reactivation_luxe", {
              source: "luxe_reactivation_popup",
              status: "error 2",
              error: res?.error?.toString(),
            });
            sendALErrorReport(
              {
                message: "Failed to accept churn offer after reactivation",
                response,
              },
              {
                name: "LuxeReactivationPopup",
                priority: "P1",
              }
            );
          }
        });
      }
    }
    return true;
  }

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const showPopup = searchParams.get(LUXE_POPUP_QUERY_PARAM);

    if (
      showPopup === "luxe" &&
      accountDetails &&
      luxe &&
      luxe?.customerSubscription?.status?.trim() === "CANCELLED"
    ) {
      setOpenLuxeReactivation(true);
    } else {
      setOpenLuxeReactivation(false);
    }
  }, [location.search, luxe, accountDetails]);

  const debouncedLuxeReactivationPopupOpen = useDebounce(isOpenLuxeReactivation, 300);

  const newPrice = (
    Number.parseFloat(luxe?.customerSubscription?.price || "39.99") -
    Number.parseFloat(luxe?.customerSubscription?.price || "39.99") * 0.3
  ).toFixed(2);

  const handleClose = useCallback(() => {
    setOpenLuxeReactivation(false);
    removeQueryParam(LUXE_POPUP_QUERY_PARAM);
  }, []);

  return (
    <ALPopup
      isOpen={debouncedLuxeReactivationPopupOpen}
      onClose={handleClose}
      className="cancel-subscription-modal"
    >
      <button
        type="button"
        className="absolute top-[20px] right-[20px] cursor-pointer"
        onClick={handleClose}
        aria-label="Close modal"
      >
        <IconClose height="15" width="15" />
      </button>
      {luxe?.customerSubscription?.status?.trim() === "CANCELLED" && (
        <div className="flex flex-col items-start">
          <h2 className="typo-h2 font-bold mb-[20px]">
            {isReactivated ? "You're back in!" : "Lock in Luxe for 30% Less"}
          </h2>
          {isReactivated ? (
            <>
              <p className="typo-body mb-[10px]">
                Your discounted rate is active. Your perks are restored and your members-only
                discount is safe.
              </p>
              <p className="typo-body mb-[25px]">Ready to put your 25% discount to work?</p>
            </>
          ) : (
            <p className="typo-body mb-[25px]">
              You’re officially saving 30% every month. Reactivate now to keep up to 25% off every
              order, 5% off Solid Gold, and stackable credit to use towards any purchase.
            </p>
          )}

          {isReactivated ? (
            <ALButtonV2 variant="primary" size="large" fullWidth>
              <ALLink to="/collections/bestsellers/">Shop Best Sellers</ALLink>
            </ALButtonV2>
          ) : (
            <>
              <ALButtonV2
                variant="primary"
                size="large"
                fullWidth
                onClick={() =>
                  reactivateLuxe({
                    cid: accountDetails.id.replace("gid://shopify/Customer/", ""),
                    email: accountDetails.email,
                    scid: luxe.customerSubscription.scid,
                  })
                }
              >
                Confirm & Save 30%
              </ALButtonV2>
              <button
                type="button"
                className="typo-body-bold underline mt-[10px]"
                onClick={handleClose}
              >
                No thanks, I’ll pay full price later
              </button>
              <p className="typo-micro italic mt-[20px]">
                By reactivating, you choose to rejoin as an AL Luxe member and agree to the{" "}
                <a
                  className="underline"
                  href="/pages/luxe-program-terms/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Luxe Program Terms
                </a>
                , including a ${newPrice} monthly payment converted to store credit. Use your credit
                as you wish, anytime. As a returning member, you'll also receive a one-time $10 gift
                gift card code upon reactivation. Offer valid for previously cancelled Luxe Members
                only and may not be combined with other reactivation offers.
              </p>
            </>
          )}
        </div>
      )}
    </ALPopup>
  );
}

export default withALErrorBoundary({
  name: "LuxeReactivationPopup",
  priority: "P1",
})(LuxeReactivationPopup);
